import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from './client.js';

type Collection<T> = { readonly data: T[]; readonly nextCursor?: string | null; readonly meta?: { readonly nextCursor?: string | null } };

// Used for selectors: never silently omit assets beyond the first page.
export const readCollection = async <T>(path: string): Promise<T[]> => {
  const items: T[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  let pages = 0;
  do {
    if (++pages > 100) throw new Error('Collection is too large to load safely');
    const result: Collection<T> = await apiRequest<Collection<T>>(path + (path.includes('?') ? '&' : '?') + 'limit=100' + (cursor === null ? '' : '&cursor=' + encodeURIComponent(cursor)));
    if (!Array.isArray(result?.data)) throw new Error('Invalid collection response');
    items.push(...result.data);
    cursor = result.meta?.nextCursor ?? result.nextCursor ?? null;
    if (cursor !== null) {
      if (seen.has(cursor)) throw new Error('Repeated collection cursor');
      seen.add(cursor);
    }
  } while (cursor !== null);
  return items;
};

export const useTenantCollection = <T>(accountId: string | null, resource: string) => {
  const path = accountId === null ? null : '/api/v1/business-accounts/' + encodeURIComponent(accountId) + '/' + resource;
  const [snapshot, setSnapshot] = useState<{ path: string; items: readonly T[] | null; error: boolean } | null>(null);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    if (path === null) return;
    let active = true;
    setSnapshot({ path, items: null, error: false });
    void apiRequest<Collection<T>>(path).then((result) => {
      if (!Array.isArray(result?.data)) throw new Error('Invalid collection response');
      if (active) setSnapshot({ path, items: result.data, error: false });
    }).catch(() => { if (active) setSnapshot({ path, items: null, error: true }); });
    return () => { active = false; };
  }, [path, revision]);
  return { items: snapshot?.path === path ? snapshot.items : null, error: snapshot?.path === path && snapshot.error, reload };
};
